import { PatsnapCopilotIntentRendererRegistry } from '@analytics/patsnap-copilot/src/patsnap-copilot-renderer';
import { CopilotPlan, type CopilotMessageContext, type PatsnapCopilotService, type SuggestionParams } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import { Site, SLD } from '@patsnap-utils/biz';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { container } from './container';
import { generateViewHref, generatePaperViewHref } from '@eureka/utils/view';
import { generateCompanyHref } from '@eureka/utils/company';
import { getCurrentLanguage } from '@eureka/language-tool';
import { CommonApis } from './apis/common.api';
import { PatsnapCopilotFeedBackService } from './patsnap-copilot-feedback-service';
import { SmartApis } from './apis/smart.api';
import { Playbooks } from './types/playbook';
import { objectToQueryParams } from '@eureka/utils/transform-url'

@injectable()
export abstract class AbstractEurekaCopilotService implements PatsnapCopilotService {
    @inject(Message)
    public readonly toast!: Message;

    @inject(PatsnapCopilotIntentRendererRegistry)
    private readonly _intentRendererRegistry!: PatsnapCopilotIntentRendererRegistry;

    @inject(PatsnapCopilotFeedBackService)
    private readonly _feedbackService!: PatsnapCopilotFeedBackService;

    patsnapCopilotUsage = {
        enabled: false,
        currentAmount: 0,
        maxAmount: 0,
        plan: CopilotPlan.A,
        webAccess: false,
    };

    container = container;

    abstract historyIdentifier(): string;

    abstract actionCallback(type: 'JUMP_TO_SOURCE', data: Record<string, unknown>): void;

    abstract pageParamFunc(): any;

    abstract contextValidator<T = any>(context: CopilotMessageContext<T>): boolean;

    abstract source: string;

    placeholder = '';
    webControl = true;

    abstract getSocketType(): string;

    abstract getSSEConfig(renderer?: string): { path: string };

    public jobId: string | null = null;

    setJobId(id: string) {
        this.jobId = id;
    }

    getJobInfo() {
        const url = `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/copilot/job`;
        const data = {
            socket_type: this.getSocketType(),
            job_id: this.jobId,
        };
        return container.RnDHttp.post({
            url,
            data,
        }).then(res => res.data);
    }

    getSocketPath() {
        return this.getJobInfo().then((info) => {
            const { job_id, sock_path } = info;
            this.setJobId(job_id);
            const path = sock_path.replace('*', job_id);
            return `${process.env.VUE_APP_EUREKA_SOCKET_URI}/eureka${path}`;
        });
    }

    track(data: {
        module_type: string,
        event_type: string,
        source_type: string,
        other_param?: string,
        event_detail_str?: string,
    }) {
        CommonApis.track(data);
    }

    disableFeedback = false;
    feedback(_data: { type: 'LIKE' | 'DISLIKE'; query: string; answer: string; question: string; intent: string; product?: string | undefined; comment?: string; }) {
        const { product, ...data } = _data;
        return this._feedbackService.send({
            product: this.source || product || '',
            ...data,
        });
    }

    generateViewHref(data: { patentId: string; params?: any }) {
        return generateViewHref(data);
    }

    generatePaperViewHref(data: { solutionId: string; }) {
        return generatePaperViewHref(data);
    }

    generateCompanyHref(context: { id: string; }) {
        return generateCompanyHref(context.id);
    }

    showResultLink(context: { product: string, [key: string]: any} | undefined) {
        return context?.product === 'eureka' && context?.query_item;
    }

    async search(query: string, type: 'patent' | 'paper', forceSemantic = false) {
        const params = {
            q: query,
            playbook: type === 'patent' ? Playbooks.SMART : Playbooks.LITERATURE,
        }
        const queryResult = await (forceSemantic ? SmartApis.previewSemantic : SmartApis.preview)(params);
        const queryString = objectToQueryParams(queryResult);
        const url = `${new Site({ sld: SLD.EUREKA, pathname: 'results/#/standard' }).href}/?${queryString}`
        window.open(url, '_blank', 'noopener')
    }

    async generateResultHref(context: { product: string, [key: string]: any} | undefined) {
        if (context?.product === 'eureka') {
            const { qid } = await CommonApis.queryItemToShort({
                q_s: context?.query_item,
            });
            return `${new Site({ sld: SLD.EUREKA }).href}/results/#/standard?playbook=${context?.playbook}&qid=${qid}`
        }
        return '';
    }

    getCurrentLanguage() {
        return getCurrentLanguage();
    }

    getSuggestion(data: SuggestionParams) {
        return container.RnDHttp.post({
            url: `${process.env.VUE_APP_EUREKA_SERVICE_URI}/eureka/smart/suggest`,
            data,
        }).then((res) => {
            return res;
        })
    }

    rendererFactory(intent: string) {
        const renderer = this._intentRendererRegistry.getRenderer({ intent });
        if (renderer) {
            return renderer;
        }
        return undefined;
    }
}
